/**
 * EmbedBridge — postMessage bridge between an embedded Thread iframe and its
 * parent window (e.g. a HubSpot blog post).
 *
 * Inbound messages (parent -> iframe):
 *   thread:start-tour   { tourId, stopAfterStep? }
 *   thread:resume-tour  { tourId, fromStep, stopAfterStep? }
 *   thread:focus-panel   { panelId }
 *
 * Outbound messages (iframe -> parent):
 *   thread:ready         { tours: Record<tourId, { steps: number, stopPoints: number[] }> }
 *   thread:tour-step     { tourId, step }
 *   thread:tour-stopped  { tourId, step, reason }
 *   thread:tour-complete { tourId }
 */

import { useEffect } from 'react';
import { useOnboarding } from './useOnboarding';
import type { TourEvent, OnboardingConfig } from './types';

interface EmbedBridgeProps {
  config?: OnboardingConfig;
}

function postToParent(type: string, payload: Record<string, unknown>) {
  window.parent.postMessage({ type, ...payload }, '*');
}

export function EmbedBridge({ config }: EmbedBridgeProps) {
  const { startTour, getStopPoints, setTourEventHandler } = useOnboarding();

  // Wire tour events to postMessage
  useEffect(() => {
    const handler = (event: TourEvent) => {
      switch (event.type) {
        case 'step':
          postToParent('thread:tour-step', { tourId: event.tourId, step: event.step });
          break;
        case 'stopped':
          postToParent('thread:tour-stopped', {
            tourId: event.tourId,
            step: event.step,
            reason: event.reason,
          });
          break;
        case 'complete':
          postToParent('thread:tour-complete', { tourId: event.tourId });
          break;
      }
    };

    setTourEventHandler(handler);
    return () => setTourEventHandler(null);
  }, [setTourEventHandler]);

  // Listen for inbound commands from parent
  useEffect(() => {
    function onMessage(e: MessageEvent) {
      const data = e.data;
      if (!data || typeof data.type !== 'string') return;

      switch (data.type) {
        case 'thread:start-tour':
          startTour(data.tourId, {
            fromBeginning: true,
            stopAfterStep: data.stopAfterStep,
          });
          break;
        case 'thread:resume-tour':
          startTour(data.tourId, {
            fromStep: data.fromStep,
            stopAfterStep: data.stopAfterStep,
          });
          break;
        case 'thread:focus-panel':
          // Handled by dispatching a custom DOM event that ProvenanceContext listens for.
          // We use postMessage relay rather than importing ProvenanceContext directly
          // to keep EmbedBridge decoupled.
          window.dispatchEvent(
            new CustomEvent('thread:focus-panel', { detail: { panelId: data.panelId } }),
          );
          break;
      }
    }

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [startTour]);

  // Send thread:ready with tour metadata once on mount
  useEffect(() => {
    const tours = config?.tours ?? {};
    const tourMeta: Record<string, { steps: number; stopPoints: number[] }> = {};
    for (const tourId of Object.keys(tours)) {
      const tourSteps = tours[tourId];
      tourMeta[tourId] = {
        steps: tourSteps?.length ?? 0,
        stopPoints: getStopPoints(tourId),
      };
    }
    postToParent('thread:ready', { tours: tourMeta });
  }, [config?.tours, getStopPoints]);

  return null;
}
